import React, { useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { Box, IconButton, Divider, Tooltip } from '@mui/material';
import {
  FormatBold, FormatItalic, FormatUnderlined, FormatListBulleted,
  FormatListNumbered, Link as LinkIcon, FormatClear,
} from '@mui/icons-material';
import { ALLOWED_TAGS, ALLOWED_ATTR } from './agent/SafeHtml';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  label?: string;
  disabled?: boolean;
  minHeight?: number;
}

// Lightweight WYSIWYG editor for "big" HTML content fields (news body, board
// decisions, letters, ...) — a contentEditable surface + a small formatting
// toolbar (execCommand, still broadly supported for these basic commands),
// sanitized through the exact same DOMPurify allow-list SafeHtml renders
// with, so what an admin types is guaranteed to be exactly what displays
// everywhere else in the app. No new dependency: reuses dompurify, already
// used for the AI agent's HTML output.
const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, label, disabled, minHeight = 160 }) => {
  const ref = useRef<HTMLDivElement>(null);
  const lastValue = useRef<string>('');

  // Only push external value changes into the DOM when they didn't originate
  // from this editor's own onInput (avoids clobbering cursor position).
  useEffect(() => {
    if (ref.current && value !== lastValue.current) {
      ref.current.innerHTML = value || '';
      lastValue.current = value || '';
    }
  }, [value]);

  const sanitizeAndEmit = () => {
    if (!ref.current) return;
    const clean = DOMPurify.sanitize(ref.current.innerHTML, {
      ALLOWED_TAGS, ALLOWED_ATTR, ALLOW_DATA_ATTR: false,
    });
    lastValue.current = clean;
    onChange(clean);
  };

  const exec = (command: string, arg?: string) => {
    ref.current?.focus();
    document.execCommand(command, false, arg);
    sanitizeAndEmit();
  };

  const handleLink = () => {
    const url = window.prompt('URL');
    if (url) exec('createLink', url);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {label && (
        <Box sx={{ fontSize: '0.8125rem', color: 'text.secondary', mb: 0.5 }}>{label}</Box>
      )}
      <Box
        sx={{
          border: '1px solid', borderColor: 'divider', borderRadius: 1,
          overflow: 'hidden', opacity: disabled ? 0.6 : 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, p: 0.5, bgcolor: 'action.hover' }}>
          <Tooltip title="Bold"><span><IconButton size="small" disabled={disabled} onMouseDown={(e) => e.preventDefault()} onClick={() => exec('bold')}><FormatBold fontSize="small" /></IconButton></span></Tooltip>
          <Tooltip title="Italic"><span><IconButton size="small" disabled={disabled} onMouseDown={(e) => e.preventDefault()} onClick={() => exec('italic')}><FormatItalic fontSize="small" /></IconButton></span></Tooltip>
          <Tooltip title="Underline"><span><IconButton size="small" disabled={disabled} onMouseDown={(e) => e.preventDefault()} onClick={() => exec('underline')}><FormatUnderlined fontSize="small" /></IconButton></span></Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Tooltip title="Bulleted list"><span><IconButton size="small" disabled={disabled} onMouseDown={(e) => e.preventDefault()} onClick={() => exec('insertUnorderedList')}><FormatListBulleted fontSize="small" /></IconButton></span></Tooltip>
          <Tooltip title="Numbered list"><span><IconButton size="small" disabled={disabled} onMouseDown={(e) => e.preventDefault()} onClick={() => exec('insertOrderedList')}><FormatListNumbered fontSize="small" /></IconButton></span></Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Tooltip title="Link"><span><IconButton size="small" disabled={disabled} onMouseDown={(e) => e.preventDefault()} onClick={handleLink}><LinkIcon fontSize="small" /></IconButton></span></Tooltip>
          <Tooltip title="Clear formatting"><span><IconButton size="small" disabled={disabled} onMouseDown={(e) => e.preventDefault()} onClick={() => exec('removeFormat')}><FormatClear fontSize="small" /></IconButton></span></Tooltip>
        </Box>
        <Box
          ref={ref}
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={sanitizeAndEmit}
          onBlur={sanitizeAndEmit}
          sx={{
            minHeight,
            maxHeight: minHeight * 3,
            overflowY: 'auto',
            p: 1.5,
            fontSize: '0.9rem',
            lineHeight: 1.7,
            outline: 'none',
            bgcolor: 'background.paper',
            '&:empty:before': { content: 'attr(data-placeholder)', color: 'text.disabled' },
            '& p': { my: 0.5 },
            '& ul, & ol': { my: 0.5, pl: 3 },
          }}
          data-placeholder="…"
        />
      </Box>
    </Box>
  );
};

export default RichTextEditor;
